import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy } from "lucide-react";
import { API } from "../../data/site";
import { inputCls, Field, DropZone, StatusPill, AdminButton } from "../ui";

const toForm = (item, fields) => {
  const f = {};
  fields.forEach((fd) => {
    const v = item?.[fd.k];
    if (fd.type === "lines" || fd.type === "images") f[fd.k] = (v || []).join("\n");
    else f[fd.k] = v ?? (fd.type === "toggle" ? fd.default ?? false : "");
  });
  fields.forEach((fd) => { if (fd.type === "toggle" && item) f[fd.k] = !!item[fd.k]; });
  return f;
};

const toPayload = (f, fields) => {
  const p = {};
  fields.forEach((fd) => {
    const v = f[fd.k];
    if (fd.type === "lines" || fd.type === "images") p[fd.k] = String(v || "").split("\n").map((s) => s.trim()).filter(Boolean);
    else if (fd.type === "number") p[fd.k] = v === "" ? null : Number(v);
    else if (fd.type === "toggle") p[fd.k] = !!v;
    else p[fd.k] = v ?? "";
  });
  return p;
};

const CollectionPanel = ({ title, singular, apiBase, fields, columns, emptyText, testid, openNewKey }) => {
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const { data: items = [], refetch } = useQuery({
    queryKey: [`${apiBase}-admin`],
    queryFn: async () => (await axios.get(`${API}/${apiBase}-admin`, { withCredentials: true })).data,
  });

  useEffect(() => { if (openNewKey) setEditing("new"); }, [openNewKey]);

  const visible = showArchived ? items : items.filter((i) => !i.archived);

  const save = async (f) => {
    const payload = toPayload(f, fields);
    setSaving(true);
    try {
      if (editing === "new") await axios.post(`${API}/${apiBase}`, payload, { withCredentials: true });
      else await axios.put(`${API}/${apiBase}/${editing.id}`, payload, { withCredentials: true });
      toast.success(`${singular} saved${payload.published ? " and live" : " as draft"}`);
      setEditing(null);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async (item) => {
    const payload = { ...item };
    delete payload.id;
    delete payload.created_at;
    payload[fields[0].k] = `${payload[fields[0].k]} (Copy)`;
    payload.published = false;
    await axios.post(`${API}/${apiBase}`, payload, { withCredentials: true });
    toast.success(`${singular} duplicated as draft`);
    refetch();
  };

  const remove = async (id) => {
    if (!window.confirm(`Permanently delete this ${singular.toLowerCase()}? This cannot be undone.`)) return;
    await axios.delete(`${API}/${apiBase}/${id}`, { withCredentials: true });
    toast.success(`${singular} deleted`);
    refetch();
  };

  const toggle = async (item, key, val, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    const payload = { ...item, [key]: val };
    delete payload.id;
    delete payload.created_at;
    await axios.put(`${API}/${apiBase}/${item.id}`, payload, { withCredentials: true });
    toast.success(val ? `${singular} published — now live on the website` : key === "published" ? `${singular} unpublished — hidden from the website` : `${singular} updated`);
    refetch();
  };

  return (
    <div data-testid={testid}>
      {editing === null && (
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <AdminButton primary onClick={() => setEditing("new")} testid={`add-${apiBase}-btn`}>
            <span className="inline-flex items-center gap-2"><Plus size={14} /> Add {singular}</span>
          </AdminButton>
          <label className="flex items-center gap-2 font-mono text-[0.62rem] tracking-[0.15em] uppercase font-bold text-[#5C5A56] cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="w-4 h-4 accent-[#8C6A4F]" data-testid={`${apiBase}-show-archived`} />
            Show Archived
          </label>
        </div>
      )}

      {editing !== null && (
        <CollectionForm
          fields={fields}
          initial={editing === "new" ? toForm(null, fields) : toForm(editing, fields)}
          title={editing === "new" ? `Add ${singular}` : `Edit — ${editing[fields[0].k]}`}
          onSave={save}
          onCancel={() => setEditing(null)}
          saving={saving}
          apiBase={apiBase}
        />
      )}

      {editing === null && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${apiBase}-table`}>
            <thead>
              <tr className="text-left border-b border-[rgba(26,26,26,0.15)]">
                {columns.map((c) => <th key={c.label} className="eyebrow text-[#5C5A56] py-3 pr-6 font-semibold">{c.label}</th>)}
                <th className="eyebrow text-[#5C5A56] py-3 pr-6 font-semibold">Status</th>
                <th className="py-3"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => (
                <tr key={item.id} className="border-b border-[rgba(26,26,26,0.08)]" data-testid={`${apiBase}-row-${item.id}`}>
                  {columns.map((c) => (
                    <td key={c.label} className={`py-4 pr-6 ${c.primary ? "font-serif text-lg" : "text-[#5C5A56] text-xs"}`}>{c.render ? c.render(item) : item[c.k] ?? "—"}</td>
                  ))}
                  <td className="py-4 pr-6"><StatusPill published={item.published !== false} archived={item.archived} testid={`${apiBase}-status-${item.id}`} /></td>
                  <td className="py-4 text-right whitespace-nowrap">
                    {!item.published || item.archived ? (
                      <button onClick={() => toggle(item, "published", true, `Publish "${item[fields[0].k]}" to the live website?`)} className="p-2 font-mono text-[0.58rem] tracking-[0.12em] uppercase font-bold text-[#4E735B] hover:underline" data-testid={`${apiBase}-publish-${item.id}`}>Publish</button>
                    ) : (
                      <button onClick={() => toggle(item, "published", false, `Unpublish "${item[fields[0].k]}"? It will be hidden from the website but not deleted.`)} className="p-2 font-mono text-[0.58rem] tracking-[0.12em] uppercase font-bold text-[#A37C45] hover:underline" data-testid={`${apiBase}-unpublish-${item.id}`}>Unpublish</button>
                    )}
                    <button onClick={() => duplicate(item)} className="p-2 hover:text-[#8C6A4F] transition-colors" data-testid={`${apiBase}-duplicate-${item.id}`} aria-label="Duplicate"><Copy size={15} /></button>
                    <button onClick={() => setEditing(item)} className="p-2 hover:text-[#8C6A4F] transition-colors" data-testid={`${apiBase}-edit-${item.id}`} aria-label="Edit"><Pencil size={15} /></button>
                    <button onClick={() => remove(item.id)} className="p-2 hover:text-red-600 transition-colors" data-testid={`${apiBase}-delete-${item.id}`} aria-label="Delete"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {visible.length === 0 && <p className="py-10 text-[#A3A09B] font-mono text-xs tracking-[0.2em] uppercase">{emptyText}</p>}
        </div>
      )}
    </div>
  );
};

const CollectionForm = ({ fields, initial, title, onSave, onCancel, saving, apiBase }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const appendLine = (k) => (url) => setF((prev) => ({ ...prev, [k]: prev[k] ? `${prev[k]}\n${url}` : url }));

  return (
    <div className="mb-10 border border-[rgba(26,26,26,0.15)] p-6 md:p-8 bg-white/50">
      <h2 className="font-serif text-2xl mb-6">{title}</h2>
      <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid={`${apiBase}-form`}>
        {fields.map((fd) => {
          if (fd.type === "textarea" || fd.type === "lines")
            return (
              <Field key={fd.k} label={fd.label} span={fd.span ?? 3} testid={`field-${fd.k}`}>
                <textarea rows={fd.rows || 3} value={f[fd.k]} onChange={set(fd.k)} className={inputCls} placeholder={fd.placeholder} data-testid={`${apiBase}-form-${fd.k}`} />
                {fd.type === "lines" && <span className="block mt-1 text-[0.65rem] text-[#A3A09B]">One per line. First image is the main photo — drag lines to reorder by editing the list.</span>}
              </Field>
            );
          if (fd.type === "images")
            return (
              <Field key={fd.k} label={fd.label} span={3} testid={`field-${fd.k}`}>
                <textarea rows={3} value={f[fd.k]} onChange={set(fd.k)} className={inputCls} placeholder={fd.placeholder} data-testid={`${apiBase}-form-${fd.k}`} />
                <span className="block mt-1 text-[0.65rem] text-[#A3A09B]">One URL per line — the first line is the main/featured image. Reorder by moving lines.</span>
                <DropZone onUploaded={appendLine(fd.k)} accept={fd.accept || "image/*"} category={fd.category} label={fd.dropLabel || "Drag & drop files here, or click to browse"} testid={`${apiBase}-form-dropzone`} />
              </Field>
            );
          if (fd.type === "toggle")
            return (
              <label key={fd.k} className="flex items-end gap-2 pb-2">
                <input type="checkbox" checked={!!f[fd.k]} onChange={set(fd.k)} className="w-4 h-4 accent-[#8C6A4F]" data-testid={`${apiBase}-form-${fd.k}`} />
                <span className="eyebrow text-[#5C5A56]">{fd.label}</span>
              </label>
            );
          if (fd.type === "select")
            return (
              <Field key={fd.k} label={fd.label} span={fd.span} testid={`field-${fd.k}`}>
                <select value={f[fd.k]} onChange={set(fd.k)} className={inputCls} data-testid={`${apiBase}-form-${fd.k}`}>
                  {fd.options.map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
            );
          return (
            <Field key={fd.k} label={fd.label} span={fd.span} testid={`field-${fd.k}`}>
              <input type={fd.type || "text"} step={fd.type === "number" ? "0.5" : undefined} required={fd.required} value={f[fd.k]} onChange={set(fd.k)} className={inputCls} placeholder={fd.placeholder} data-testid={`${apiBase}-form-${fd.k}`} />
            </Field>
          );
        })}
        <div className="md:col-span-3 flex gap-3 pt-2">
          <AdminButton primary testid={`${apiBase}-form-save`} disabled={saving}>{saving ? "Saving…" : "Save"}</AdminButton>
          <AdminButton onClick={onCancel} testid={`${apiBase}-form-cancel`}>Cancel</AdminButton>
        </div>
      </form>
    </div>
  );
};

export default CollectionPanel;
